import { Close, Restore } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import Alert from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { Loader, Modal } from '../../../components';
import { postActivationNotice } from '../../../fetch';
import { isEmail } from '../../../helpers';
import { IActivationNotice } from '../../../models';
import { DatePicker } from '@mui/x-date-pickers';

interface IActivationNoticeModal {
  activationNotice: IActivationNotice;
  open: boolean;
  onClose: () => void;
  fetchNeighborhoods: () => void;
}

export const ActivationNoticeModal: FC<IActivationNoticeModal> = ({
  open,
  onClose,
  activationNotice,
  fetchNeighborhoods
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isResending, setResending] = useState(false);
  const [agentIds, setAgentIds] = useState(
    activationNotice.agents
      .filter(agent => agent.isAssignedToNeighborhood)
      .map(agent => agent.personId)
  );
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [presaleStartDate, setPresaleStartDate] = useState(null);
  const [additionalDetails, setAdditionalDetails] = useState('');

  const isDesktop = useMediaQuery('(min-width: 960px');

  const handleClose = () => {
    setAdditionalEmails('');
    setPresaleStartDate(null);
    setAdditionalDetails('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} maxWidth="lg">
      <Fade in={open}>
        <div>
          {isResending && (
            <Loader position="centered" type="overlay">
              Resending...
            </Loader>
          )}
          <Box marginBottom="1rem">
            {activationNotice && (
              <Grid container spacing={1}>
                <Grid item xs={12} md={9}>
                  <Typography variant="h5">Resend Neighborhood Activation Email</Typography>
                  <Typography variant="subtitle1">
                    <span className={classes.emphasis}>Neighborhood: </span>
                    {activationNotice.neighborhoodName}
                  </Typography>
                  <Typography variant="subtitle1">
                    <span className={classes.emphasis}>Phase: </span>
                    {activationNotice.phase}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
          <Alert severity="warning">
            This function will send an email to the specifed recipients that includes all the
            information about a neighborhood to prepare for sales.
          </Alert>
          <Grid container spacing={1}>
            <Grid item xs={12} className={classes.clearSelections}>
              <Button
                id="reset"
                color="primary"
                variant="contained"
                startIcon={<Close />}
                disabled={agentIds.length < 1}
                onClick={() => setAgentIds([])}
              >
                Clear Selections
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.flexContainer} flexWrap={'wrap'}>
                {activationNotice.agents &&
                  activationNotice.agents.map((agent, index) => (
                    <FormControl
                      key={`key-${agent.personId}-${index}`}
                      id={`form-control-${agent.personId}-${index}`}
                      style={isDesktop ? { width: '50%' } : { width: '100%' }}
                      className={classes.flexItem}
                    >
                      <FormControlLabel
                        color="primary"
                        control={
                          <Checkbox
                            checked={agentIds.includes(agent.personId)}
                            id="orders-checkbox"
                            color="primary"
                            onClick={e => {
                              e.stopPropagation();
                              agentIds.includes(agent.personId)
                                ? setAgentIds(
                                    agentIds.filter(selectedAgent => {
                                      return selectedAgent !== agent.personId;
                                    })
                                  )
                                : setAgentIds([...agentIds, agent.personId]);
                            }}
                          />
                        }
                        label={`${agent.firstName} ${agent.lastName} - ${agent.email}`}
                      />
                    </FormControl>
                  ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                name="AdditionalAddresses"
                autoComplete="none"
                label="Additional Addresses (Separate with ; if needed)"
                value={additionalEmails}
                error={
                  !!additionalEmails &&
                  additionalEmails
                    // Removes all instances of Whitespace characters. Just using " " only removes one space, not multiple.
                    .replace(/\s+/g, '')
                    .split(';')
                    .some(email => !isEmail(email))
                }
                helperText={'This must be a list of emails separated by a ";" (semi-colon)'}
                onBlur={e => {}}
                onChange={e => {
                  setAdditionalEmails(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <DatePicker
                  label={'Presale Start Date'}
                  value={presaleStartDate}
                  onChange={selectedDate => {
                    if (selectedDate === null) {
                      setPresaleStartDate(null);
                      return;
                    }
                    setPresaleStartDate(selectedDate);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      value: presaleStartDate
                    }
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                multiline
                minRows={3}
                fullWidth
                variant="outlined"
                size="small"
                name="additionalDetails"
                autoComplete="none"
                label="Additional Details - This text will appear in the body of the email"
                value={additionalDetails}
                onBlur={e => {}}
                onChange={e => {
                  setAdditionalDetails(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ButtonGroup>
                <Button
                  id="resend"
                  color="primary"
                  variant="contained"
                  disabled={
                    isResending ||
                    agentIds.length < 1 ||
                    (!!additionalEmails &&
                      additionalEmails
                        // Removes all instances of Whitespace characters. Just using " " only removes one space, not multiple.
                        .replace(/\s+/g, '')
                        .split(';')
                        .some(email => !isEmail(email)))
                  }
                  startIcon={<Restore />}
                  onClick={async () => {
                    setResending(true);
                    try {
                      await postActivationNotice(activationNotice.neighborhoodId, {
                        agentIds,
                        // Removes all instances of Whitespace characters. Just using " " only removes one space, not multiple.
                        additionalEmails: additionalEmails
                          ? additionalEmails.replace(/\s+/g, '')
                          : null,
                        presaleStartDate: presaleStartDate ? presaleStartDate.toISOString() : null,
                        additionalDetails: additionalDetails.length !== 0 ? additionalDetails : null
                      });
                      enqueueSnackbar(`Neighborhood Activation Notice Resent!`, {
                        variant: 'success'
                      });
                      handleClose();
                      fetchNeighborhoods();
                    } catch (error) {
                      console.log(error);
                      if (
                        error &&
                        error.response &&
                        error.response.data &&
                        error.response.data.Detail
                      ) {
                        enqueueSnackbar(error.response.data.Detail, {
                          variant: 'error'
                        });
                      } else {
                        enqueueSnackbar(`Error resending notice, please try again.`, {
                          variant: 'error'
                        });
                      }
                    } finally {
                      setResending(false);
                    }
                  }}
                >
                  Resend Notice
                </Button>

                <Button
                  id="cancel"
                  color="inherit"
                  variant="contained"
                  startIcon={<Close />}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </div>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },

  flexContainer: {
    margin: theme.spacing(1),
    flexDirection: 'column'
  },
  flexItem: {
    width: '50%'
  },
  emphasis: {
    fontWeight: 'bold'
  },
  clearSelections: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    '@media (min-width: 600px)': {
      justifyContent: 'flex-end'
    }
  }
}));
